/**
 * Theme manager: applies dark/light mode based on user preference or OS setting,
 * and re-applies the persisted high-contrast class. Runs synchronously before
 * first paint (called from main.tsx) to avoid a flash of incorrect theme.
 *
 * Storage keys:
 *  - lupuslogix-theme: "system" | "light" | "dark"  (default: "system")
 *  - lupuslogix-high-contrast: "true" | "false"
 */

const THEME_KEY = "lupuslogix-theme";
const CONTRAST_KEY = "lupuslogix-high-contrast";
const COOKIE_KEY = "lupuslogix_theme";
const IDB_NAME = "lupuslogix-prefs";
const IDB_STORE = "kv";

export type ThemePreference = "system" | "light" | "dark";

const isValidPref = (v: unknown): v is ThemePreference =>
  v === "light" || v === "dark" || v === "system";

// ---------- Cookie helpers (synchronous, survives localStorage purges) ----------
const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(name + "="));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
};

const writeCookie = (name: string, value: string) => {
  if (typeof document === "undefined") return;
  try {
    // 1 year, root path, lax. Secure attr in https only.
    const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=31536000; path=/; SameSite=Lax${secure}`;
  } catch {
    // ignore
  }
};

// ---------- IndexedDB helpers (async, durable across iOS storage pressure) ----------
const openIDB = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

const idbGet = async (key: string): Promise<string | null> => {
  const db = await openIDB();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve(typeof req.result === "string" ? req.result : null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

const idbSet = (key: string, value: string): void => {
  void openIDB().then((db) => {
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, key);
    } catch {
      // ignore
    }
  });
};

const getSystemPrefersDark = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

/**
 * Synchronous read priority: localStorage → cookie. IndexedDB is async and is
 * used only as a recovery source (see `recoverThemeFromIDB`). This ordering
 * lets `initializeTheme()` run before first paint without async waits.
 */
export const getStoredThemePreference = (): ThemePreference => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (isValidPref(v)) return v;
  } catch {
    // ignore (private mode, etc.)
  }
  const c = readCookie(COOKIE_KEY);
  if (isValidPref(c)) {
    // Reseed localStorage if it was cleared.
    try { localStorage.setItem(THEME_KEY, c); } catch { /* ignore */ }
    return c;
  }
  return "system";
};

export const setThemePreference = (pref: ThemePreference) => {
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {
    // ignore
  }
  writeCookie(COOKIE_KEY, pref);
  idbSet(THEME_KEY, pref);
  applyTheme(pref);
};

const applyTheme = (pref: ThemePreference) => {
  const root = document.documentElement;
  const useDark = pref === "dark" || (pref === "system" && getSystemPrefersDark());
  root.classList.toggle("dark", useDark);

  // Re-apply high-contrast (it depends on .dark for the .dark.high-contrast variant)
  let contrast = false;
  try {
    contrast = localStorage.getItem(CONTRAST_KEY) === "true";
  } catch {
    // ignore
  }
  root.classList.toggle("high-contrast", contrast);
};

/**
 * Async recovery: if both localStorage and cookie were wiped (rare, but
 * happens on iOS under storage pressure), restore from IndexedDB.
 * Called once after first paint; never blocks initial render.
 */
const recoverThemeFromIDB = async () => {
  try {
    if (isValidPref(readCookie(COOKIE_KEY))) return;
    try { if (isValidPref(localStorage.getItem(THEME_KEY))) return; } catch { /* ignore */ }
    const stored = await idbGet(THEME_KEY);
    if (isValidPref(stored)) {
      try { localStorage.setItem(THEME_KEY, stored); } catch { /* ignore */ }
      writeCookie(COOKIE_KEY, stored);
      applyTheme(stored);
    }
  } catch {
    // ignore
  }
};

let mediaListenerAttached = false;
let lifecycleListenersAttached = false;

export const initializeTheme = () => {
  const pref = getStoredThemePreference();
  applyTheme(pref);
  // Async recovery from IDB if the sync sources came up empty.
  if (typeof window !== "undefined") {
    setTimeout(() => { void recoverThemeFromIDB(); }, 0);
  }

  // Listen for OS theme changes — only relevant when user is on "system"
  if (!mediaListenerAttached && typeof window !== "undefined" && window.matchMedia) {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if (getStoredThemePreference() === "system") {
        applyTheme("system");
      }
    };
    // Modern browsers
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", handler);
    } else if (typeof (mq as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener === "function") {
      // Safari < 14 fallback
      (mq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(handler);
    }
    mediaListenerAttached = true;
  }

  // Re-apply theme when the app resumes from background (iOS PWA quirk:
  // standalone apps can lose the .dark class after suspension, and
  // prefers-color-scheme may briefly evaluate stale on resume).
  // Reported by beta tester Brittny Spaid — explicit "dark" preference
  // intermittently reverted to light after returning to the PWA.
  if (!lifecycleListenersAttached && typeof document !== "undefined") {
    const reapply = () => applyTheme(getStoredThemePreference());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") reapply();
    });
    window.addEventListener("pageshow", reapply);
    window.addEventListener("focus", reapply);
    lifecycleListenersAttached = true;
  }
};

// ---------- Debug helpers (consumed by ThemeDebugPanel) ----------
export interface ThemeDebugSnapshot {
  storedPref: ThemePreference;
  rawLocalStorage: string | null;
  rawCookie: string | null;
  rawIndexedDB: string | null;
  systemPrefersDark: boolean;
  htmlHasDarkClass: boolean;
  highContrast: boolean;
  isStandalonePWA: boolean;
  capturedAt: string;
}

export const captureThemeDebugSnapshot = async (): Promise<ThemeDebugSnapshot> => {
  let raw: string | null = null;
  try { raw = localStorage.getItem(THEME_KEY); } catch { /* ignore */ }
  const cookie = readCookie(COOKIE_KEY);
  const idb = await idbGet(THEME_KEY);
  const standalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true);
  return {
    storedPref: getStoredThemePreference(),
    rawLocalStorage: raw,
    rawCookie: cookie,
    rawIndexedDB: idb,
    systemPrefersDark: getSystemPrefersDark(),
    htmlHasDarkClass:
      typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
    highContrast: getHighContrast(),
    isStandalonePWA: !!standalone,
    capturedAt: new Date().toISOString(),
  };
};

export const setHighContrast = (enabled: boolean) => {
  try {
    localStorage.setItem(CONTRAST_KEY, String(enabled));
  } catch {
    // ignore
  }
  document.documentElement.classList.toggle("high-contrast", enabled);
};

export const getHighContrast = (): boolean => {
  try {
    return localStorage.getItem(CONTRAST_KEY) === "true";
  } catch {
    return false;
  }
};